import type { JourneyMessage } from "@hightouch/lib/llm/schema";

import { isScheduleConfig, type Schedule } from "src/components/schedule/types";
import {
  DEFAULT_INTERVAL_SCHEDULE,
  EMPTY_CONTEXT_CONFIG,
  NULL_EXIT_CRITERIA,
} from "src/pages/journeys/constants";
import type { JourneyContextConfig } from "src/pages/journeys/types";
import type { JourneyExitCriteriaConfig } from "src/types/journeys";

import { isJourneyGraphEnvelopeContent } from "./journey-output-adapter";

export type JourneyOutputInitialConfig = {
  contextConfig: JourneyContextConfig;
  exitCriteria: JourneyExitCriteriaConfig;
  schedule: Schedule | null;
};

export const getJourneyOutputInitialConfig = ({
  currentJourney,
  isNewJourney,
  journeyOutput,
}: {
  currentJourney: object;
  isNewJourney: boolean;
  journeyOutput?: JourneyMessage["content"];
}): JourneyOutputInitialConfig => {
  const isEnvelopeOutput =
    journeyOutput && isJourneyGraphEnvelopeContent(journeyOutput);

  const isRealtime =
    "execution_mode" in currentJourney &&
    currentJourney.execution_mode === "realtime";
  const scheduleOrDefault = (value: Schedule | null | undefined) =>
    isRealtime ? (value ?? null) : (value ?? DEFAULT_INTERVAL_SCHEDULE);

  if (isNewJourney && !isEnvelopeOutput) {
    return {
      exitCriteria: NULL_EXIT_CRITERIA,
      schedule: DEFAULT_INTERVAL_SCHEDULE,
      contextConfig: EMPTY_CONTEXT_CONFIG,
    };
  }

  const schedule =
    "schedule" in currentJourney ? currentJourney.schedule : null;
  const exitCriteria =
    "exit_criteria" in currentJourney ? currentJourney.exit_criteria : null;
  const contextConfig =
    "context_config" in currentJourney ? currentJourney.context_config : null;

  if (!isEnvelopeOutput) {
    return {
      exitCriteria:
        (exitCriteria as JourneyExitCriteriaConfig | null) ??
        NULL_EXIT_CRITERIA,
      schedule: scheduleOrDefault(schedule as Schedule | null),
      contextConfig:
        (contextConfig as JourneyContextConfig | null) ?? EMPTY_CONTEXT_CONFIG,
    };
  }

  if (schedule != null && !isScheduleConfig(schedule)) {
    throw new Error("Journey schedule has an invalid shape");
  }

  if (
    exitCriteria != null &&
    !(
      typeof exitCriteria === "object" &&
      "type" in exitCriteria &&
      (exitCriteria.type === "and" || exitCriteria.type === "or") &&
      "conditions" in exitCriteria &&
      Array.isArray(exitCriteria.conditions)
    )
  ) {
    throw new Error("Journey exit criteria has an invalid shape");
  }

  if (
    contextConfig != null &&
    !(
      typeof contextConfig === "object" &&
      "variables" in contextConfig &&
      Array.isArray(contextConfig.variables)
    )
  ) {
    throw new Error("Journey context config has an invalid shape");
  }

  return {
    exitCriteria:
      (exitCriteria as JourneyExitCriteriaConfig | null) ?? NULL_EXIT_CRITERIA,
    schedule: scheduleOrDefault(schedule as Schedule | null),
    contextConfig:
      (contextConfig as JourneyContextConfig | null) ?? EMPTY_CONTEXT_CONFIG,
  };
};
